/**
 * Groups consecutive same-city cruise days into one stay — mirrors iOS CruiseCityStayGrouping.
 */

import type { Trip, TripCityVisitMeta } from '../types/client';
import { isAtSeaLocation } from '../services/cruiseItinerariesApi';

export interface CruiseCityVisitRef {
  id: string;
  cityName: string;
  arrivalDate: string;
  isEmbarkation: boolean;
  isDebarkation: boolean;
}

export interface CruiseCityStay {
  visits: CruiseCityVisitRef[];
}

function sameCityName(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

export function cruiseVisitRefsFromTrip(trip: Trip): CruiseCityVisitRef[] {
  const metaMap = trip.cityVisitMeta ?? {};
  const names = trip.cityNames ?? {};
  return (trip.cityIds ?? []).map((id) => {
    const meta: TripCityVisitMeta | undefined = metaMap[id];
    return {
      id,
      cityName: names[id]?.trim() || '',
      arrivalDate: meta?.arrivalDate ?? '',
      isEmbarkation: Boolean(meta?.isEmbarkation),
      isDebarkation: Boolean(meta?.isDebarkation),
    };
  });
}

/** Consecutive same-name port days. At Sea breaks a stay and is omitted. */
export function cruiseCityStays(trip: Trip): CruiseCityStay[] {
  const visits = cruiseVisitRefsFromTrip(trip).slice().sort((a, b) => {
    if (a.arrivalDate && b.arrivalDate && a.arrivalDate !== b.arrivalDate) {
      return a.arrivalDate.localeCompare(b.arrivalDate);
    }
    return 0;
  });

  if (trip.tripType !== 'cruise') {
    return visits
      .filter((v) => !isAtSeaLocation(v.cityName))
      .map((v) => ({ visits: [v] }));
  }

  const result: CruiseCityStay[] = [];
  let current: CruiseCityVisitRef[] = [];

  const closeCurrent = (): void => {
    if (current.length === 0) return;
    result.push({ visits: current });
    current = [];
  };

  for (const visit of visits) {
    if (isAtSeaLocation(visit.cityName)) {
      closeCurrent();
      continue;
    }
    if (current.length > 0 && sameCityName(current[current.length - 1].cityName, visit.cityName)) {
      current.push(visit);
    } else {
      closeCurrent();
      current = [visit];
    }
  }
  closeCurrent();
  return result;
}

export function cruiseCityStayContaining(trip: Trip, visitId: string): CruiseCityStay {
  const match = cruiseCityStays(trip).find((stay) => stay.visits.some((v) => v.id === visitId));
  if (match) return match;
  const visit = cruiseVisitRefsFromTrip(trip).find((v) => v.id === visitId);
  return { visits: visit ? [visit] : [] };
}
